import React, {useEffect, useState} from "react";
import {connect} from "react-redux";
import 'chartjs-plugin-zoom';
import {Button, Paper, Typography, withStyles} from "@material-ui/core";
import * as actions from "./store/kpiActions";
import AppLogoLoading from "../../sc-logo";
import {Bar, Doughnut} from "react-chartjs-2";
import moment from "moment";
import DataRange from "../../sc-utilities/DateRange";
import momentTimezone from 'moment-timezone';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        zIndex: 1000,
        paddingTop: theme.spacing(),
        paddingLeft: theme.spacing(),
        marginBottom: theme.spacing(),
        marginTop: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    loading: {
        marginLeft: 'auto',
        marginRight: 'auto',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
    },
    chartTotal: {
        textAlign: 'center',
        width: '100%',
        padding: theme.spacing(1),
    },
    productChartButtons: {
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        marginLeft: theme.spacing(5)
    },
    chart: {
        marginTop: theme.spacing(),
        textAlign: 'center',
        width: '100%',
        padding: theme.spacing(1),
    },
    androidIOSChart: {
        width: '100%',
        display: 'flex'
    },
    mobileChart: {
        marginTop: theme.spacing(),
        textAlign: 'center',
        width: '50%',
        padding: theme.spacing(1),
    }
});

const SuccessRatio = (props) => {
    const { classes, translations, fetchDeviceStatistics, ActiveCustomerID, refetchTab, setRefetchTab } = props;

    const [data, setData] = useState();

    const [totalBarChartData, setTotalBarChartData] = useState();
    const [productTypeChartTitle, setProductTypeChartTitle] = useState();
    const [productChartData, setProductChartData] = useState();
    const [androidIOSChartData, setAndoidIOSChartData] = useState();

    const [loading, setLoading] = useState(false);

    const [dateRangeValue, setDateRangeValue] = useState(null);

    const fetchSuccessRatioData = async () => {
        setLoading(true);
        setProductTypeChartTitle(null);
        const result = await fetchDeviceStatistics({ ActiveCustomerID, From: dateRangeValue?.From.format('YYYY-MM-DD'), To: dateRangeValue?.To.format('YYYY-MM-DD') });
        if (result) {
            setData(result);
            setChartData(result);
        }
        setRefetchTab(false);
        setLoading(false);
    }

    useEffect(() => {
        const customerTimezone = window.localStorage.getItem("CustomerTimezone") ?? 'UTC';

        const from = momentTimezone.tz(moment().subtract(7, 'days').set({
            hours: 0,
            minutes: 0,
            seconds: 0
        }), customerTimezone);

        const to = momentTimezone.tz(moment(), customerTimezone);
        
        setDateRangeValue({
            From: from,
            To: to,
            maxDate: to.set({
                hours: 23,
                minutes: 59,
                seconds: 59,
            })
        });
        // eslint-disable-next-line
    }, [ ActiveCustomerID ]);

    useEffect(() => {
        setChartData(data ?? []);
        if (dateRangeValue && refetchTab) {
            fetchSuccessRatioData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetchTab, dateRangeValue])

    useEffect(() => {
        dateRangeValue && fetchSuccessRatioData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRangeValue])

    const successTranslation = translations["success"] || "Success";
    const failedTranslation = translations["failed"] || "Failed";
    const abortedTranslation = translations["aborted"] || "Aborted";

    const pcProductTranslation = translations['pc_product'] || 'PC Product';
    const mobileProductTranslation = translations['mobile_product'] || 'Mobile Product';
    const storageProductTranslation = translations['storage_product'] || 'Storage Product';

    const getTotalRateByProductType = (data) => ({
        labels: [pcProductTranslation, mobileProductTranslation, storageProductTranslation],
            datasets: [
            {
                label: successTranslation,
                data: Object.keys(data?.type || {})?.map((type) => data.type[type]?.["success"]),
                backgroundColor: 'green',
            },
            {
                label: failedTranslation,
                data: Object.keys(data?.type || {})?.map((type) => data.type[type]?.["failed"]),
                backgroundColor: '#ff4d4d',
            },
            {
                label: abortedTranslation,
                data: Object.keys(data?.type || {})?.map((type) => data.type[type]?.["aborted"]),
                backgroundColor: '#ffff4d',
            },
        ]
    })

    const setChartData = (data) => {
        setTotalBarChartData(getTotalRateByProductType(data));
    };

    const setMobileProductOSData = () => {
        setProductChartData(null);
        setProductTypeChartTitle(mobileProductTranslation);
        const chartData = [{
            labels: [successTranslation, failedTranslation, abortedTranslation],
            datasets: [
                {
                    label: "IOS",
                    data: [data.type["MobileProduct"]["ios"]?.["success"], data.type["MobileProduct"]["ios"]?.["failed"], data.type["MobileProduct"]["ios"]?.["aborted"]],
                    backgroundColor: ['#26C281', '#ff4d4d', '#ffff4d'],
                }
            ]
        },
            {
                labels: [successTranslation, failedTranslation, abortedTranslation],
                datasets: [
                    {
                        label: "Android",
                        data: [data.type["MobileProduct"]["android"]?.["success"], data.type["MobileProduct"]["android"]?.["failed"], data.type["MobileProduct"]["android"]?.["aborted"]],
                        backgroundColor: ['#26C281', '#ff4d4d', '#ffff4d'],
                    }
                ]
            }]
        setAndoidIOSChartData(chartData);
    }

    const setPCorStorageProductData = (productType) => {
        setAndoidIOSChartData(null);
        setProductTypeChartTitle(productType==="PCProduct"? pcProductTranslation : storageProductTranslation);
        const chartData = {
            labels: [successTranslation, failedTranslation, abortedTranslation],
            datasets: [
                {
                    label: successTranslation,
                    data: [data.type[productType]?.["success"], data.type[productType]?.["failed"], data.type[productType]?.["aborted"]],
                    backgroundColor: ['#26C281', '#ff4d4d', '#ffff4d'],
                }
            ]
        }
        setProductChartData(chartData);
    }

    const TotalBarChart = (
        <div className={classes.chartTotal}>
            <Typography variant="h6">{translations["total"] || "Total"}</Typography>
            <Bar height={90} data={totalBarChartData || {}} redraw={false} options={{
                responsive: true,
            }} />
            <div className={classes.productChartButtons}>
                <Button variant={"contained"} onClick={() => setPCorStorageProductData("PCProduct")}>{pcProductTranslation}</Button>
                <Button variant={"contained"} onClick={() => setMobileProductOSData()}>{mobileProductTranslation}</Button>
                <Button variant={"contained"} onClick={() => setPCorStorageProductData("StorageProduct")}>{storageProductTranslation}</Button>
            </div>
        </div>
    )

    const ProductChart = (ChartType) => (
        <div className={classes.chart}>
            <Typography variant="h6">{productTypeChartTitle}</Typography>
            <ChartType height={90} data={productChartData || {}} redraw={false} options={{
                responsive: true,
            }} />
        </div>
    )

    const AndroidIOSProductChart = (ChartType, index) => (
        <div className={classes.mobileChart}>
            <Typography variant="h6">{index===0? "IOS" : "Android"}</Typography>
            <ChartType height={150} data={androidIOSChartData?.[index] || {}} redraw={false} options={{
                responsive: true,
            }} />
        </div>
    )

    const filterDataRange = range => {
        setDateRangeValue({ From: range.From, To: range.To });
    };

    return (
        <Paper className={classes.root}>
            {dateRangeValue && <DataRange
                initialValues={dateRangeValue}
                filterDataRange={filterDataRange}
                value={dateRangeValue}
                setValue={setDateRangeValue}
                format="yyyy/MM/dd 00:00:00"
                translations={translations}
            />}
            {loading ? <div className={classes.loading}><AppLogoLoading  /></div> :
            <>
                {TotalBarChart}
                {productChartData && productTypeChartTitle && !androidIOSChartData && ProductChart(Doughnut)}
                <div className={classes.androidIOSChart}>
                    {androidIOSChartData && AndroidIOSProductChart(Doughnut, 0)}
                    {androidIOSChartData && AndroidIOSProductChart(Doughnut, 1)}
                </div>
            </>}
        </Paper>
    )
}

const mapDispatchToProps = dispatch => {
    return {
        fetchDeviceStatistics: (params) => dispatch(actions.fetchDeviceStatistics(params))
    };
};

const mapStateToProps = state => {
    return {
        ActiveCustomerID: state?.core?.login?.activeCustomer?.CustomerID || null,
        permissions: state?.core?.login?.permissions || [],
        columns: state?.core?.login?.userSettings?.Columns || null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {}
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(SuccessRatio));
